import { AvatarDropdown, AvatarName, SelectLang } from '@/components';
import { getToken, validateToken } from '@/services/user';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { UserProvider } from '@/contexts/UserContext';
import { getStoredToken } from '@/services/authService';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  loading?: boolean;
  currentUser?: any;
}> {
  // 获取当前路径
  const { location } = history;
  const currentPath = location.pathname;

  // 如果在登录页面，直接返回设置
  if (currentPath === loginPath) {
    return {
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  // 检查登录状态
  const token = getStoredToken();
  let storedUser = null;

  try {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsed = JSON.parse(userData);
      storedUser = parsed
        ? { ...parsed, role: (parsed.role || '').trim().toLowerCase() }
        : null;
    }
  } catch (error) {
    console.error('Failed to parse stored user data:', error);
  }

  // 如果没有token或用户数据，重定向到登录页面
  if (!token || !storedUser) {
    // 使用setTimeout避免在初始化期间立即重定向
    setTimeout(() => {
      history.push(loginPath);
    }, 0);
    return {
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  return {
    settings: defaultSettings as Partial<LayoutSettings>,
    currentUser: storedUser,
  };
}

async function authenticate() {
  const stored_token = localStorage.getItem('USER_TOKEN');
  if (!stored_token) {
    const { jwt_token } = await getToken();
    localStorage.setItem('USER_TOKEN', jwt_token);
  } else {
    const res = await validateToken({
      jwt_token: stored_token,
    });
    if (!res.status) {
      history.push('/404');
    }
  }
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: () => [<SelectLang key="SelectLang" />],
    avatarProps: {
      src: '',
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    onPageChange: () => {
      const { location } = history;
      const token = getStoredToken();

      // 如果不在登录页面且没有token，重定向到登录页面
      if (location.pathname !== loginPath && !token) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [],
    links: isDev ? [] : [],
    menuHeaderRender: undefined,
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <UserProvider>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </UserProvider>
      );
    },
    // ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,

  // dev: 继续走 /api，让 proxy.ts 生效（不会有 CORS 问题）
  // build/sandbox/prod: 走 API_BASE_URL（execute-api），避免请求落到 CloudFront/S3
  prefix: isDev ? '/api' : API_BASE_URL,

  // 请求拦截器：添加 Authorization header 和用户信息
  requestInterceptors: [
    (url: string, options: any) => {
      // 为API请求添加Authorization header
      if (url?.startsWith('/api/') || url?.startsWith('/webhook/')) {
        // 优先使用环境变量的API_TOKEN，如果没有则使用localStorage中的token
        const envApiToken = typeof API_TOKEN !== 'undefined' ? API_TOKEN : undefined;
        const localToken = localStorage.getItem('auth_token');
        // 如果都没有，使用硬编码的默认值（仅用于开发环境）
        const defaultToken = 'UBS_API_Tech';
        const token = envApiToken || localToken || defaultToken;

        if (token) {
          // 确保headers对象存在
          if (!options.headers) {
            options.headers = {};
          }
          // 添加Authorization header
          options.headers['Authorization'] = `Bearer ${token}`;
          
          // 添加用户信息到请求头（用于权限检查）
          try {
            const userData = localStorage.getItem('user');
            if (userData) {
              const user = JSON.parse(userData);
              // 只发送必要的用户信息（角色和用户名）
              options.headers['X-User-Info'] = JSON.stringify({
                id: user.id,
                username: user.username,
                role: user.role,
                permissions: user.permissions || [], // 包含权限以便后端验证
              });
            }
          } catch (error) {
            console.warn('Failed to add user info to request header:', error);
          }
        }
      }
      
      return { url, options };
    },
  ],
};
